import React, { useState, useEffect, useRef, ReactElement, Suspense, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { animated, useSpring } from '@react-spring/web';
import { ErrorBoundary } from 'react-error-boundary';
import Notification from '@Common/Notification';
import Routes from '../../constants/routes';
import { DashboardsTab } from '../../styles/shared-styles';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import Openings from './components/Openings/Openings';
import OverviewFilterSpeedDial from './components/Filters/OverviewFilterSpeedDial';
import TodaysFocus from './components/TodaysFocus/TodaysFocus';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import InputText from '@Common/InputText';
import TaskListOption from '../TaskList/TaskListOption';
import Modal61 from '@Common/Modal/Modal61';
import CustomizeWidgetsDrawer from '../CorporateDashboard/CustomizeWidgetsDrawer';
import ErrorFallback from '../Common/ErrorFallback';
import { isRegularUser, isCustomerAdminOrOwner } from '../../helpers/accountHelper';
import { CORPORATE_DASHBOARD_DEFAULT, CORPORATE_DASHBOARD_DEFAULT_MANAGE, OVERVIEW_DEFAULT_TABS } from '../../constants/constants';
import { Modal61Ref } from '@Types/common';
import { Tab } from './components/types';
import ConfigDrawer from './Modals/ConfigDrawer';
import {
    useLoadDashboardFilters,
    useLoadDashboardSettings,
    useGetOverviewTabsConfig,
    useSaveSettings,
    useUpdateSettings,
    useUpdateOverviewTabsConfig,
    useSetOverviewTab,
    useCheckSettingsAvailability,
    DashboardSetting,
} from '../../apis/companyDashboard';
import { useLoadProjectListFilters } from '../../apis/project';
import './Overview.scss';

const { CORPORATE_DASHBOARD_VIEW_DEFAULT_ID, SAVE_CURRENT_VIEW_TEXT, SAVE_CURRENT_VIEW_ID } = CORPORATE_DASHBOARD_DEFAULT;
const { SAVE_CURRENT_VIEW_MANAGE_TEXT, SAVE_CURRENT_VIEW_MANAGE_ID } = CORPORATE_DASHBOARD_DEFAULT_MANAGE;

export interface ProjectOption {
    id: number;
    name: string;
    abbreviation: string;
    description: string;
}

const defaultSettings = {
    id: undefined as string | undefined,
    name: undefined as string | undefined,
    updates: 0, //this is used to track state changes from customize widget drawer so child components can know when to update without a deep compare
    isProjectSummariesActive: true,
    isLocationSummariesActive: true,
    isMyTasksDueSoonActive: true,
    isMilestonesMissedActive: true,
    brandIds: [],
    areaIds: [],
    regionIds: [],
    managementCompanyIds: [],
    locationTypeIds: [],
    continentIds: [],
    isMilestonesOnly: false,
    disciplineIds: [],
    departmentIds: [],
    phaseIds: [],
    communityIds: [],
    baselineIds: [],
    projectIds: [],
    locationIds: [],
    tagIds: [],
};

const defaultTabsConfig: Array<Tab> = [
    {
        id: OVERVIEW_DEFAULT_TABS.TodaysFocus.id,
        label: OVERVIEW_DEFAULT_TABS.TodaysFocus.label,
        icon: OVERVIEW_DEFAULT_TABS.TodaysFocus.icon,
        selectedProjectIds: [],
    },
    {
        id: OVERVIEW_DEFAULT_TABS.Openings.id,
        label: OVERVIEW_DEFAULT_TABS.Openings.label,
        icon: OVERVIEW_DEFAULT_TABS.Openings.icon,
        selectedProjectIds: [],
    },
];

const Overview = (): ReactElement => {
    const [tabs, setTabs] = useState<Array<Tab>>([]);
    const [activeTab, setActiveTab] = useState<Tab>(defaultTabsConfig[0]);
    const [selectedSettings, setSelectedSettings] = useState<DashboardSetting>(defaultSettings);
    const [selectedSettingsId, setSelectedSettingsId] = useState<string>('defaultId');
    const [matchingSettings, setMatchingSettings] = useState<DashboardSetting[]>([]);
    const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false);
    const saveFiltersModalRef = useRef<Modal61Ref>(null);
    const { customerRole, currentSubscriptionId } = useSelector((state: any) => state.user);
    const history = useHistory();

    // ---- TanStack Query — all three initial fetches run in parallel via Suspense ----
    const { data: filtersData } = useLoadDashboardFilters();
    const { data: settingsData } = useLoadDashboardSettings();
    const { data: tabsConfigData } = useGetOverviewTabsConfig();
    const { data: projectFiltersData } = useLoadProjectListFilters(currentSubscriptionId?.toString() ?? '');

    // ---- Mutations ----
    const saveSettingsMutation = useSaveSettings(() => {
        Notification('success', 'Settings Updated and Saved', '');
        saveFiltersModalRef.current?.hide();
    });
    const updateSettingsMutation = useUpdateSettings(() => {
        Notification('success', 'Settings Updated and Saved', '');
        saveFiltersModalRef.current?.hide();
    });
    const updateTabsConfigMutation = useUpdateOverviewTabsConfig();
    const setOverviewTabMutation = useSetOverviewTab();
    const checkAvailabilityMutation = useCheckSettingsAvailability();

    // ---- Derived data ----
    const availableProjects = projectFiltersData?.projectOptionsDropdownList?.map((p) => p.option) ?? [];
    const userFavoriteProjects = projectFiltersData?.userFavoriteProjects ?? [];

    // ---- Build tab component ----
    // Called at render time (not cached in tabs state) so dashboardSettings changes reach
    // children immediately. Keys intentionally exclude `settings` — including it would remount
    // the child on every filter change and wipe its local state; TanStack query-key changes
    // (inside TodaysFocus/Openings) already handle refetching when settings change.
    const buildTabComponent = useCallback((tab: Tab, settings: DashboardSetting) => {
        switch (tab.id) {
            case OVERVIEW_DEFAULT_TABS.TodaysFocus.id: {
                const ids = Array.isArray(tab.selectedProjectIds) ? [...tab.selectedProjectIds] : [];
                return <TodaysFocus key={`todays-focus-${tab.id}-${ids.join('-')}`} selectedProjectIds={ids} dashboardSettings={settings} />;
            }
            case OVERVIEW_DEFAULT_TABS.Openings.id: {
                const ids = Array.isArray(tab.selectedProjectIds) ? [...tab.selectedProjectIds] : [];
                const sectionIds = Array.isArray(tab.selectedSectionIds) ? [...tab.selectedSectionIds] : [];
                return (
                    <Openings
                        key={`openings-${tab.id}-${ids.join('-')}-${sectionIds.join('-')}`}
                        projectIds={ids}
                        selectedSectionIds={sectionIds}
                        dashboardSettings={settings}
                    />
                );
            }
            default:
                return <></>;
        }
    }, []);

    // ---- Initialise tabs from server config (runs once when data arrives) ----
    useEffect(() => {
        document.title = 'Overview';

        let tabsConfig = [...defaultTabsConfig];

        if (tabsConfigData.overviewConfig.tabs) {
            tabsConfig = defaultTabsConfig.map((defConfig) => {
                const saved = tabsConfigData.overviewConfig.tabs!.find((t) => t.id === defConfig.id);
                // Today's Focus is mandatory and non-configurable — its label must never be
                // overridden by saved server config.
                const isTodaysFocus = defConfig.id === OVERVIEW_DEFAULT_TABS.TodaysFocus.id;
                return saved
                    ? {
                        ...defConfig,
                        label: isTodaysFocus ? defConfig.label : saved.label,
                        selectedProjectIds: saved.selectedProjectIds,
                        selectedSectionIds: saved.selectedSectionIds ?? [],
                    }
                    : defConfig;
            });
        }

        setTabs(tabsConfig);

        if (tabsConfigData.selectedTabId != null) {
            const selected = tabsConfig.find((tab) => tab.id === tabsConfigData.selectedTabId);
            if (selected) setActiveTab(selected);
        }
    }, [tabsConfigData]);

    // ---- Initialise selected settings from server data (runs once) ----
    useEffect(() => {
        if (settingsData.length === 0) return;
        const first = settingsData[0];
        const firstId = first.id ?? CORPORATE_DASHBOARD_VIEW_DEFAULT_ID;
        setSelectedSettingsId(firstId);
        setSelectedSettings(firstId === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID ? defaultSettings : first);
    }, [settingsData]);

    // ---- Apply default project selection for tabs without one ----
    useEffect(() => {
        if (!isInitialLoad || availableProjects.length === 0 || tabs.length === 0) return;
        // Today's Focus is not configurable and is never project-filtered, so it never needs a default.
        const isConfigurable = (tab: Tab) => tab.id !== OVERVIEW_DEFAULT_TABS.TodaysFocus.id;
        const needsDefault = tabs.some((tab) => isConfigurable(tab) && (!tab.selectedProjectIds || tab.selectedProjectIds.length === 0));
        if (!needsDefault) return;

        const defaultProjectIds: number[] =
            userFavoriteProjects.length > 0
                ? [userFavoriteProjects[0].id]
                : availableProjects.length > 0
                    ? [availableProjects[0].id]
                    : [];

        const updatedTabs = tabs.map((tab) => {
            if (isConfigurable(tab) && (!tab.selectedProjectIds || tab.selectedProjectIds.length === 0)) {
                return { ...tab, selectedProjectIds: defaultProjectIds };
            }
            return tab;
        });

        setTabs(updatedTabs);

        const activeIndex = updatedTabs.findIndex((t) => t.id === activeTab?.id);
        if (activeIndex !== -1) setActiveTab(updatedTabs[activeIndex]);

        setIsInitialLoad(false);
    }, [isInitialLoad, availableProjects, userFavoriteProjects, tabs, activeTab]);

    // ---- Handlers ----

    const onTabClick = (tab: Tab) => {
        setOverviewTabMutation.mutate({ overviewTabId: tab.id, openingsSubTabId: undefined });
        setActiveTab(tab);
    };

    const onSettingsMenuChange = (id: string) => {
        const found = settingsData.find((s) => s?.id === id) ?? defaultSettings;
        setSelectedSettings(found);
        setSelectedSettingsId(id);
    };

    const getSettingsMenuOptions = () => {
        const options: Array<{ id: any; name: any; itemIcon: string; isSeparator?: boolean }> = settingsData.map((s) => ({
            id: s.id ?? CORPORATE_DASHBOARD_VIEW_DEFAULT_ID,
            name: s.name ?? 'Unsaved View',
            itemIcon: 'list-check',
        }));

        const hasDefault = options.some((o) => o.id === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID);
        if (!hasDefault) {
            options.push({ itemIcon: 'plus', id: CORPORATE_DASHBOARD_VIEW_DEFAULT_ID, name: 'Create new view', isSeparator: true });
        }

        options.push({ itemIcon: 'pen', id: SAVE_CURRENT_VIEW_MANAGE_ID, name: SAVE_CURRENT_VIEW_MANAGE_TEXT, isSeparator: !!hasDefault });
        options.push({ itemIcon: 'floppy-disk', id: SAVE_CURRENT_VIEW_ID, name: SAVE_CURRENT_VIEW_TEXT, isSeparator: !!hasDefault });

        return options;
    };

    const handleSettingsMenuChange = (menuItemValue: string) => {
        if (menuItemValue === SAVE_CURRENT_VIEW_ID) {
            saveFiltersModalRef.current?.show();
        } else if (menuItemValue === SAVE_CURRENT_VIEW_MANAGE_ID) {
            history.push(Routes.ManageCorporateViews);
        } else {
            onSettingsMenuChange(menuItemValue);
        }
    };

    const onSaveCustomFilters = () => {
        const settings = { ...selectedSettings };
        if (settings.id === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID) settings.id = undefined;
        setIsSaving(true);

        const onSettled = () => setIsSaving(false);
        const onError = (err: Error) => Notification('error', 'Error', err.message);

        if (settings.id) {
            updateSettingsMutation.mutate({ settings, id: settings.id }, { onSettled, onError });
        } else {
            saveSettingsMutation.mutate(settings, {
                onSuccess: (data) => {
                    if (data?.id) {
                        setSelectedSettings((prev) => ({ ...prev, id: data.id }));
                        setSelectedSettingsId(data.id);
                    }
                },
                onSettled,
                onError,
            });
        }
    };

    const onCompleteCustomizerDrawer = (newSettings: DashboardSetting) => {
        setSelectedSettings(newSettings);
        setSelectedSettingsId(newSettings.id ?? 'defaultId');
    };

    const checkAvailability = (settings: DashboardSetting) => {
        checkAvailabilityMutation.mutate(settings, {
            onSuccess: (data) => setMatchingSettings(data),
            onError: (err) => Notification('error', 'Error', err.message),
        });
    };

    const saveConfig = (tabId: number, tabLabel: string, selectedProjectIds: number[], selectedSectionIds: string[]): Promise<void> => {
        return new Promise((resolve) => {
            updateTabsConfigMutation.mutate(
                { tabId, tabLabel, selectedProjectIds, selectedSectionIds },
                {
                    onSuccess: () => {
                        Notification('success', 'Settings Updated and Saved', '');

                        const tabsConfig = [...tabs];
                        const tabIndex = tabsConfig.findIndex((t) => t.id === tabId);

                        if (tabIndex !== -1) {
                            const updatedTab = { ...tabsConfig[tabIndex], label: tabLabel, selectedProjectIds, selectedSectionIds };
                            tabsConfig[tabIndex] = updatedTab;
                            setTabs(tabsConfig);
                            if (activeTab?.id === tabId) setActiveTab(updatedTab);
                        }

                        setIsConfigDrawerOpen(false);
                        resolve();
                    },
                    onError: (err) => {
                        Notification('error', 'Error', err.message);
                        resolve();
                    },
                }
            );
        });
    };

    const renderConfigIcon = () => {
        // Today's Focus is a mandatory, non-configurable tab — no gear.
        if (activeTab?.id === OVERVIEW_DEFAULT_TABS.TodaysFocus.id) return null;
        if (isCustomerAdminOrOwner(customerRole)) {
            return <i className='mb-1 fa-regular fa-gear' onClick={() => setIsConfigDrawerOpen(true)}></i>;
        } else if (!isRegularUser(customerRole)) {
            return <i className='mb-1 fa-regular fa-gear not-allowed'></i>;
        }
    };

    const updateView = selectedSettings.id && selectedSettings.id !== CORPORATE_DASHBOARD_VIEW_DEFAULT_ID;
    const panelAnimation = useSpring({ transform: isSettingsDrawerOpen ? 'translateX(0%)' : 'translateX(100%)' });

    return (
        <LoadingIndicator isLoading={isSaving} hideWhileLoading={false} isFullscreen={false}>
            <animated.div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: 'min(380px, 100vw)',
                    height: '100vh',
                    background: '#eee',
                    boxShadow: '-2px 0 5px rgba(0, 0, 0, 0.2)',
                    padding: '20px',
                    zIndex: 999,
                    ...panelAnimation,
                }}>
                <CustomizeWidgetsDrawer
                    onComplete={onCompleteCustomizerDrawer}
                    onClose={() => setIsSettingsDrawerOpen(false)}
                    settings={selectedSettings}
                    defaultSettings={defaultSettings}
                    filters={filtersData.filters}
                    isSubscriptionProjectWidgetActive={filtersData.isSubscriptionProjectWidgetActive}
                    matchingSettings={matchingSettings}
                    checkAvailability={checkAvailability}
                />
            </animated.div>

            <div className='overview'>
                <div className='row g-0 align-items-end mb-4'>
                    <div className='col-12 col-lg-6'>
                        <DashboardsTab className='nav nav-tabs'>
                            {tabs.map((tab) => (
                                <li key={tab.id} className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab?.id === tab.id ? 'active' : ''}`}
                                        onClick={() => onTabClick(tab)}
                                        type='button'>
                                        <i className={`fa-regular ${tab.icon}`} />
                                        <span>{tab.label}</span>
                                    </a>
                                </li>
                            ))}
                        </DashboardsTab>
                    </div>

                    <div className='col-12 col-md-6 col-lg-2 nav nav-tabs pb-2 pe-4'>
                        <div className='input-group'>
                            <input className='form-control border-end-0 border rounded' type='search' placeholder='search' id='search' />
                            <span className='input-group-append search-icon'>
                                <button className='btn btn-outline-secondary bg-white border-start-0 border-bottom-0 border' type='button'>
                                    <i className='far fa-search'></i>
                                </button>
                            </span>
                        </div>
                    </div>

                    <div className='col-6 col-md-3 col-lg-2 nav nav-tabs pb-2'>
                        <MultiSelectBox
                            name='filtersView'
                            idField='id'
                            valueField='name'
                            options={getSettingsMenuOptions()}
                            onChange={handleSettingsMenuChange}
                            selectedValues={selectedSettingsId}
                            isMulti={false}
                            isSearchable={false}
                            optionComponent={TaskListOption}
                            shouldShowIcons={false}
                            className='mb-0 w-100'
                        />
                    </div>

                    <div className='col-3 col-md-2 col-lg-1 nav nav-tabs pb-2'>
                        <button
                            type='button'
                            className='btn'
                            onClick={() => setIsSettingsDrawerOpen(true)}>
                            <i className='fa-regular fa-bars-filter'></i>
                            <span className='ms-2 show-filters'>Filters</span>
                        </button>
                    </div>

                    <div className='col-3 col-md-1 col-lg-1 nav nav-tabs pb-2 tab-context-actions fa-xl'>{renderConfigIcon()}</div>
                </div>

                <div className='row'>
                    <div className='col-12'>
                        {(() => {
                            const activeTabEntry = tabs.find((tab) => tab.id === activeTab?.id);
                            return activeTabEntry ? buildTabComponent(activeTabEntry, selectedSettings) : null;
                        })()}
                    </div>
                </div>
            </div>

            {/* Modals */}

            <Modal61
                title='Save View'
                ref={saveFiltersModalRef}
                isCentered={true}
                size='md'
                body={
                    <form name='saveViewForm' noValidate>
                        <div className='form-group'>
                            <label htmlFor='custom-filters-name' className='title'>
                                Custom View Name
                            </label>
                            <InputText
                                name='custom-filters-name'
                                type='text'
                                value={selectedSettings.name ?? ''}
                                onChange={(e) => setSelectedSettings((prev) => ({ ...prev, name: e.target.value }))}
                                isRequired={true}
                                errorMessage='Custom View Name is required'
                                maxLength={20}
                                placeholder={updateView ? 'Would you like to rename this view?' : 'What would you like to name this view?'}
                            />
                            <small id='custom-view-name-help' className='form-text text-muted sub-title'>
                                Character Limit 20
                            </small>
                        </div>

                        {matchingSettings.length > 0 && (
                            <div className='alert alert-warning'>
                                <p>A view with the same filters already exists.</p>
                                <p>Are you sure you want to continue?</p>
                            </div>
                        )}
                    </form>
                }
                primaryButtonText={updateView ? 'Update' : 'Save'}
                onPrimaryClick={onSaveCustomFilters}
            />

            {isConfigDrawerOpen && (
                <ConfigDrawer
                    tab={activeTab}
                    availableProjects={availableProjects}
                    onSave={saveConfig}
                    onClose={() => setIsConfigDrawerOpen(false)}
                />
            )}
            {/* Hidden — not unmounted — while a drawer is open: unmounting would throw away
                any filter selections staged in the dialogs but not yet applied. The settings
                drawer uses the legacy Drawer (which never sets body.modal-open), so the CSS
                rule alone cannot cover it. */}
            <OverviewFilterSpeedDial
                isHidden={isSettingsDrawerOpen || isConfigDrawerOpen}
                filters={filtersData.filters}
                settings={selectedSettings}
                defaultSettings={defaultSettings}
                onApply={onCompleteCustomizerDrawer}
            />

        </LoadingIndicator>
    );
};

const OverviewWithSuspense = (): ReactElement => (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingIndicator isLoading={true} hideWhileLoading={true} isFullscreen={true}><></></LoadingIndicator>}>
            <Overview />
        </Suspense>
    </ErrorBoundary>
);

export default OverviewWithSuspense;
